import { useState } from "react";
import { ArrowLeft, ChevronDown, Copy, ExternalLink, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { Site } from "@/bryntum-lib/models/Site";
import { cn } from "@/modern/utils";
import { useCustomDomain } from "../hooks/useCustomDomain";
import type { DnsRecordInstruction } from "../types/domain";
import { domainStatusLabel } from "../utils";
import { SitePublishUrlRow } from "./SitePublishUrlRow";

function DnsRecordRow({ label, record }: { label: string; record: DnsRecordInstruction }) {
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-1 rounded-md border border-border/80 bg-background/60 p-2.5">
      <p className="text-[11px] font-medium text-foreground">{label}</p>
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 text-[10px] uppercase text-muted-foreground">{record.record_type}</span>
        <code className="min-w-0 flex-1 truncate text-[11px]">{record.host}</code>
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => copy(record.host)} aria-label={`Copy ${label} host`}>
          <Copy className="size-3" />
        </Button>
      </div>
      <div className="flex items-center gap-1.5">
        <code className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">{record.value}</code>
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => copy(record.value)} aria-label={`Copy ${label} value`}>
          <Copy className="size-3" />
        </Button>
      </div>
    </div>
  );
}

interface SitePublishDomainBlockProps {
  site: Site;
  boardSlug: string | undefined;
  boardId: number | undefined;
  isReadOnly: boolean;
  isBusy: boolean;
  onDomainChanged?: () => void;
}

export function SitePublishDomainBlock({
  site,
  boardSlug,
  boardId,
  isReadOnly,
  isBusy,
  onDomainChanged,
}: SitePublishDomainBlockProps) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(false);

  const {
    status,
    domainInput,
    setDomainInput,
    instructions,
    lastError,
    isBusy: domainBusy,
    handleSave,
    handleConfirm,
    handleRemove,
    handleChangeDomain,
    hasDomainApi,
  } = useCustomDomain({
    site,
    boardSlug,
    boardId,
    isReadOnly,
    onDomainChanged,
  });

  const busy = isBusy || domainBusy;
  const connectUrl = instructions?.domainConnectUrl;
  const liveDomainUrl = site.domain?.trim() ? `https://${site.domain.replace(/^https?:\/\//, "")}` : "";
  const hasDnsInstructions =
    Boolean(instructions?.validationRecords.length) || Boolean(instructions?.routingCname);

  if (!hasDomainApi) {
    return <p className="text-xs text-muted-foreground">Domain setup will appear when the board is ready.</p>;
  }

  const statusChip = (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
        status === "active" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
        status === "failed" && "border-destructive/30 bg-destructive/10 text-destructive",
        status === "pending_verification" && "border-amber-500/30 bg-amber-500/10 text-amber-700",
        status === "provisioning" && "border-primary/30 bg-primary/10 text-primary",
        status === "unset" && "border-border bg-muted/40 text-muted-foreground",
      )}
    >
      {domainStatusLabel(status)}
    </span>
  );

  if (status === "active" && liveDomainUrl) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          {statusChip}
          {!isReadOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => void handleRemove()}
              disabled={busy}
            >
              <Trash2 className="size-3" />
              Remove
            </Button>
          )}
        </div>
        <SitePublishUrlRow url={liveDomainUrl} />
        {site.canonicalUrl?.trim() && (
          <p className="text-[11px] text-muted-foreground">
            Canonical: <span className="font-mono text-foreground/80">{site.canonicalUrl.replace(/^https?:\/\//, "")}</span>
          </p>
        )}
      </div>
    );
  }

  const showSetup = setupOpen || status === "failed" || (status === "unset" && domainInput.trim().length > 0);

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        {statusChip}
        {status === "unset" && !isReadOnly && !showSetup && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-primary"
            onClick={() => setSetupOpen(true)}
          >
            <Link2 className="size-3" />
            Add custom domain
          </Button>
        )}
      </div>

      {site.domain && status !== "unset" && (
        <p className="truncate font-mono text-xs text-foreground">{site.domain}</p>
      )}

      {status === "failed" && lastError && (
        <p className="text-xs leading-relaxed text-destructive">{lastError}</p>
      )}

      {status === "provisioning" && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 shrink-0 animate-spin" />
          Provisioning SSL and routing…
        </p>
      )}

      {showSetup && (status === "unset" || status === "failed") && !isReadOnly && (
        <div className="space-y-2 rounded-lg border border-border/80 bg-muted/20 p-2.5">
          <p className="text-[11px] text-muted-foreground">Visitors will reach your site at your own hostname after DNS verification.</p>
          <div className="flex gap-1.5">
            <Input
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="www.yourcompany.com"
              variant="outline"
              inputSize="sm"
              className="h-8 font-mono text-xs"
              disabled={busy}
            />
            <Button type="button" size="sm" className="h-8 shrink-0 px-3" onClick={() => void handleSave()} disabled={busy}>
              {domainBusy ? <Loader2 className="size-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      )}

      {status === "pending_verification" && (
        <Collapsible open={dnsOpen} onOpenChange={setDnsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-left text-xs font-medium text-amber-800 dark:text-amber-200"
            >
              <span>DNS records required</span>
              <ChevronDown className={cn("size-3.5 transition-transform", dnsOpen && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {instructions && hasDnsInstructions ? (
              <>
                {connectUrl && !isReadOnly && (
                  <Button type="button" variant="outline" size="sm" className="h-8 w-full gap-1.5 text-xs" asChild>
                    <a href={connectUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="size-3" />
                      Connect with registrar
                    </a>
                  </Button>
                )}
                {instructions.validationRecords.map((record, index) => (
                  <DnsRecordRow
                    key={`${record.host}-${record.record_type}-${index}`}
                    label={instructions.validationRecords.length > 1 ? `Validation ${index + 1}` : "Certificate validation"}
                    record={record}
                  />
                ))}
                {instructions.routingCname && <DnsRecordRow label="Routing (CNAME)" record={instructions.routingCname} />}
                {!isReadOnly && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => void handleChangeDomain()} disabled={busy}>
                      <ArrowLeft className="size-3" />
                      Change
                    </Button>
                    <Button type="button" size="sm" className="h-8 flex-1 text-xs" variant="secondary" onClick={() => void handleConfirm()} disabled={busy}>
                      {domainBusy ? <Loader2 className="size-3.5 animate-spin" /> : "I've added DNS records"}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 shrink-0 animate-spin" />
                Loading DNS instructions…
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
